import { apiService } from '../services/api';
import {
  prepareImageDataUrl,
  prepareImageFile,
  prepareInlineImageDataUrl,
  type PrepareImageOptions,
} from './imageUpload';
import { preferSameOriginSchoolMediaUrl } from './schoolMediaUrl';

export type MediaOwnerType =
  | 'student'
  | 'staff'
  | 'visitor'
  | 'tenant'
  | 'signature'
  | 'promo'
  | 'website'
  | 'assignment'
  | 'student_document'
  | 'expense';

/** Matches backend `media::tenant_brand_owner_uuid` (Uuid::from_u128(tenant_id)). */
export function tenantBrandOwnerUuid(tenantId: number): string {
  const hex = BigInt(tenantId).toString(16).padStart(32, '0');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface TenantMediaUploadResult {
  mediaId: string;
  previewUrl: string;
}

export type UploadImageOptions = PrepareImageOptions & {
  ownerType: MediaOwnerType;
  ownerId: string;
  fieldName?: string;
  academicYear?: string;
  storageSlot?: string;
  /** Super-admin: scope upload to a tenant (`x-tenant-id`). */
  tenantId?: string | number;
};

/**
 * Optimize then upload an image through the shared media API.
 * Preserves pixel dimensions; reduces file size only when the source is unusually large.
 */
export async function uploadOptimizedImage(
  source: File | Blob | string,
  options: UploadImageOptions
): Promise<TenantMediaUploadResult | null> {
  const filename =
    options.filename ??
    (source instanceof File && source.name.trim() ? source.name : 'photo.jpg');
  const prepared = await prepareImageFile(source, { filename });

  try {
    const upload = await apiService.uploadImage(prepared, {
      ownerType: options.ownerType,
      ownerId: options.ownerId,
      fieldName: options.fieldName,
      academicYear: options.academicYear,
      storageSlot: options.storageSlot,
      tenantId: options.tenantId,
    });
    const mediaId = upload.data?.media_id;
    if (!mediaId) return null;

    try {
      const urlRes = await apiService.getMediaUrl(String(mediaId), options.tenantId);
      const previewUrl = urlRes.data?.url?.trim();
      if (previewUrl) {
        return { mediaId: String(mediaId), previewUrl };
      }
    } catch {
      /* fall through */
    }
    return { mediaId: String(mediaId), previewUrl: '' };
  } catch {
    return null;
  }
}

export async function uploadTenantMedia(
  file: File | Blob | string,
  ownerType: MediaOwnerType,
  ownerId: string,
  filename?: string
): Promise<TenantMediaUploadResult | null> {
  return uploadOptimizedImage(file, { ownerType, ownerId, filename });
}

/** Upload when cloud storage is configured; otherwise return an optimized data URL. */
export async function uploadProfilePhoto(
  source: File | Blob | string,
  ownerType: MediaOwnerType,
  ownerId: string,
  tenantId?: string | number,
  storageSlot?: string
): Promise<{ mediaId: string | null; previewUrl: string }> {
  const uploaded = await uploadOptimizedImage(source, { ownerType, ownerId, tenantId, storageSlot });
  if (uploaded) {
    const previewUrl =
      uploaded.previewUrl ||
      (await resolveMediaDisplayUrl(uploaded.mediaId, undefined, tenantId)) ||
      '';
    return { mediaId: uploaded.mediaId, previewUrl };
  }

  const previewUrl =
    ownerType === 'visitor'
      ? await prepareInlineImageDataUrl(source)
      : await prepareImageDataUrl(source);
  return { mediaId: null, previewUrl };
}

const urlCache = new Map<string, { url: string; expiresAt: number }>();

export async function resolveMediaDisplayUrl(
  mediaId: string | null | undefined,
  fallbackUrl?: string | null,
  /** Super-admin: scope lookup to a tenant (`x-tenant-id`). */
  tenantId?: string | number
): Promise<string> {
  const fallback = (fallbackUrl ?? '').trim();
  const id = (mediaId ?? '').trim();
  if (!id) {
    return fallback;
  }

  const cacheKey = tenantId != null && String(tenantId).trim() ? `${id}@${String(tenantId).trim()}` : id;
  const cached = urlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  try {
    const res = await apiService.getMediaUrl(id, tenantId);
    const url = res.data?.url?.trim();
    if (url) {
      // Same-origin `/school-media/...` avoids cross-origin MinIO CORS and keeps canvas JPG export clean.
      const displayUrl = preferSameOriginSchoolMediaUrl(url);
      const ttlMs = Math.max(30_000, (res.data?.expires_in_seconds ?? 300) * 1000 - 10_000);
      urlCache.set(cacheKey, { url: displayUrl, expiresAt: Date.now() + ttlMs });
      return displayUrl;
    }
  } catch {
    /* use fallback */
  }
  return preferSameOriginSchoolMediaUrl(fallback) || fallback;
}

export function isLikelyMediaId(value: string | null | undefined): boolean {
  const v = (value ?? '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

/** True for short-lived S3/MinIO GET URLs that must not be stored in `logo_url`. */
export function isEphemeralMediaUrl(url: string | null | undefined): boolean {
  const u = (url ?? '').trim();
  if (!u) return false;
  return /[?&]X-Amz-/i.test(u) || /[?&]x-id=GetObject/i.test(u);
}

/**
 * Value to persist as `logo_url` on school/tenant profile save.
 * Prefer `logo_media_id` (fresh signed URLs are minted on read); never store presigned URLs.
 */
export function persistentLogoUrlForSave(
  logoUrl: string | null | undefined,
  logoMediaId: string | null | undefined
): string | null {
  if ((logoMediaId ?? '').trim()) return null;
  const url = (logoUrl ?? '').trim();
  if (!url || isEphemeralMediaUrl(url)) return null;
  return url;
}

/** Upload a deferred photo after the entity row exists (student/staff create flows). */
export async function uploadPendingEntityPhoto(
  file: File | null | undefined,
  ownerType: Extract<MediaOwnerType, 'student' | 'staff'>,
  entityId: string
): Promise<{ photo_media_id: string | null; image_url: string | null }> {
  if (!file) {
    return { photo_media_id: null, image_url: null };
  }
  const result = await uploadProfilePhoto(file, ownerType, entityId);
  return {
    photo_media_id: result.mediaId,
    image_url: result.mediaId ? null : result.previewUrl || null,
  };
}

/** Upload tenant-scoped branding assets (logo, signatures, promo slots). */
export async function uploadTenantBrandAsset(
  file: File,
  ownerType: Extract<MediaOwnerType, 'tenant' | 'signature' | 'promo'>,
  tenantId: number,
  storageSlot = 'logo'
): Promise<{ mediaId: string; previewUrl: string }> {
  if (!Number.isFinite(tenantId) || tenantId <= 0) {
    throw new Error('Tenant id is required for cloud upload.');
  }
  const ownerId = tenantBrandOwnerUuid(tenantId);
  const uploaded = await uploadOptimizedImage(file, {
    ownerType,
    ownerId,
    storageSlot,
    tenantId,
  });
  if (!uploaded) {
    throw new Error('Cloud upload is unavailable. Enable S3 storage on the backend or use a direct URL.');
  }
  const previewUrl =
    uploaded.previewUrl || (await resolveMediaDisplayUrl(uploaded.mediaId, undefined, tenantId)) || '';
  return { mediaId: uploaded.mediaId, previewUrl };
}

const ACCEPTED_DOCUMENT_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

export function isAcceptedStudentDocument(file: File): boolean {
  const type = file.type.trim().toLowerCase();
  if (ACCEPTED_DOCUMENT_TYPES.has(type)) return true;
  const name = file.name.toLowerCase();
  return name.endsWith('.pdf') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
}

/** Upload a scanned student document (PDF or image) after the student row exists. */
export async function uploadStudentDocument(
  file: File,
  studentId: string
): Promise<string | null> {
  return uploadSupportingDocument(file, studentId, 'student_document');
}

/** Upload an optional expense receipt after the expense row exists. */
export async function uploadExpenseDocument(
  file: File,
  expenseId: string
): Promise<string | null> {
  return uploadSupportingDocument(file, expenseId, 'expense');
}

async function uploadSupportingDocument(
  file: File,
  ownerId: string,
  ownerType: Extract<MediaOwnerType, 'student_document' | 'expense'>
): Promise<string | null> {
  if (!isAcceptedStudentDocument(file)) {
    throw new Error('Only PDF, JPEG, PNG, or WebP files are allowed.');
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('Each document must be 10 MB or smaller.');
  }
  const upload = await apiService.uploadDocument(file, { ownerId, ownerType });
  return upload.data?.media_id ? String(upload.data.media_id) : null;
}
